// Single source of truth for rendering money in the UI.
//
// The debit-matcher stores two sides on FX-matched transactions:
//   amount / currency        — INR (what the bank charged)
//   amountFx / currencyFx    — foreign (what the vendor billed)
//
// Ops-facing views should show the BILLING side by default — a €533.97
// Vueling booking is a €533.97 booking, not a ₹45,381 booking. Bank-
// reconciliation views (statements, matcher audit) should show the
// bank side. The `variant` argument picks which.

export type Money = {
  amount: string | null | undefined;
  currency: string | null | undefined;
  amountFx?: string | null;
  currencyFx?: string | null;
};

const CURRENCY_SYMBOL: Record<string, string> = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$',
  SGD: 'S$', HKD: 'HK$', CHF: 'CHF ', CNY: '¥', THB: '฿', MYR: 'RM',
  KRW: '₩', IDR: 'Rp', VND: '₫', AED: 'د.إ ', ZAR: 'R', NZD: 'NZ$',
  SEK: 'kr ', PLN: 'zł ', TRY: '₺', DKK: 'kr ', NOK: 'kr ',
};

export function currencySymbol(code: string | null | undefined): string {
  if (!code) return '';
  const upper = code.toUpperCase();
  return CURRENCY_SYMBOL[upper] ?? `${upper} `;
}

function formatNumber(n: number, currency: string): string {
  const locale = currency.toUpperCase() === 'INR' ? 'en-IN' : 'en-US';
  return n.toLocaleString(locale, { maximumFractionDigits: 2 });
}

/**
 * Which side is meaningful given the row and the caller's context.
 *
 *   billing (default) → the foreign side when the debit was FX_MATCHED;
 *                       otherwise falls back to the primary amount.
 *   bank              → always the primary `amount / currency` (INR-side
 *                       after FX match). For bank-statement reconciliation.
 *   both              → "€533.97 · debited ₹45,381" when FX; otherwise
 *                       just the primary side. Great for headers.
 */
export function formatMoney(
  money: Money | null | undefined,
  variant: 'billing' | 'bank' | 'both' = 'billing',
): string {
  if (!money) return '—';
  const primary = parseFloat(money.amount ?? '');
  const primaryCcy = money.currency ?? 'INR';
  if (!Number.isFinite(primary)) return '—';

  const primaryLabel = `${currencySymbol(primaryCcy)}${formatNumber(primary, primaryCcy)}`;

  const fx = parseFloat(money.amountFx ?? '');
  const fxCcy = money.currencyFx;
  const hasFx = Number.isFinite(fx) && fxCcy && fxCcy.toUpperCase() !== primaryCcy.toUpperCase();
  if (!hasFx) return primaryLabel;

  const fxLabel = `${currencySymbol(fxCcy)}${formatNumber(fx, fxCcy)}`;

  switch (variant) {
    case 'billing': return fxLabel;
    case 'bank':    return primaryLabel;
    case 'both':    return `${fxLabel} · debited ${primaryLabel}`;
  }
}

/** For places that only need the numeric magnitude of the billing side
 *  (e.g. sorting, filtering, computing totals). Returns 0 when invalid. */
export function billingAmountNumber(money: Money | null | undefined): number {
  if (!money) return 0;
  const fx = parseFloat(money.amountFx ?? '');
  const fxCcy = money.currencyFx;
  const primary = parseFloat(money.amount ?? '');
  const primaryCcy = money.currency ?? 'INR';
  if (Number.isFinite(fx) && fxCcy && fxCcy.toUpperCase() !== primaryCcy.toUpperCase()) return fx;
  return Number.isFinite(primary) ? primary : 0;
}

export function billingCurrency(money: Money | null | undefined): string {
  if (!money) return 'INR';
  const fxCcy = money.currencyFx;
  const primaryCcy = money.currency ?? 'INR';
  if (fxCcy && parseFloat(money.amountFx ?? '') > 0 && fxCcy.toUpperCase() !== primaryCcy.toUpperCase()) {
    return fxCcy;
  }
  return primaryCcy;
}
